import { HttpClient, HttpContext, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError, timer, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_AUTH_REFRESH } from '../interceptors/auth-expired.interceptor';

export type PlatformAdminMfaChannel = 'EMAIL' | 'AUTHENTICATOR_APP' | 'SMS';

export interface MfaDeliveryCapabilities {
  smsAvailable: boolean;
  smsDestination: string | null;
}

export type PlatformAdminSession = {
  authenticated: boolean;
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  accountType?: string | null;
  roles?: string[] | null;
};

export type PlatformAdminPreAuthResponse = {
  message?: string | null;
  userId: string;
  email?: string | null;
  mfaEnabled: boolean;
  validated: boolean;
  authenticatorConfigured?: boolean | null;
  preAuthToken?: string | null;
  challengeId?: string | null;
};

export type PlatformAdminMfaInitiateResponse = {
  statusCode?: number;
  message?: string;
  sent?: boolean;
  channel?: string | null;
  challengeId?: string | null;
  userId?: string | null;
  email?: string | null;
};

export type PlatformAdminVerifyMfaResponse = {
  message?: string | null;
  statusCode: number;
  authCookieCount?: number | null;
  authCookieNames?: string | null;
  user?: {
    roles?: string[] | null;
    userId?: string | null;
    accountType?: string | null;
  } | null;
};

export type OrganizationContextItem = {
  orgId: string;
  orgName: string;
  status?: string | null;
  portalSlug?: string | null;
  orgRole?: string | null;
  orgType?: string | null;
  portalMode?: string | null;
  landingPath?: string | null;
  accessScopeType?: string | null;
  assignedPropertyIds?: string[] | null;
  owner?: boolean | null;
  staff?: boolean | null;
  subscriptionActive?: boolean | null;
  permissions?: string[] | null;
};

export type OrganizationContextResponse = {
  hasMembership?: boolean;
  defaultOrgId?: string | null;
  organizations?: OrganizationContextItem[] | null;
};

export type PlatformOrganization = {
  id: string;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactPersonFirstName?: string | null;
  contactPersonLastName?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  website?: string | null;
  timeZone?: string | null;
  currency?: string | null;
  status?: string | null;
  portalSlug?: string | null;
  orgType?: string | null;
  primaryDomain?: string | null;
  domainStatus?: string | null;
  createdDate?: string | null;
  deletedAt?: string | null;
  lastModifiedDate?: string | null;
  members?: unknown[] | null;
};

export type PlatformOrganizationCreateRequest = {
  name: string;
  portalSlug?: string | null;
  orgType?: string | null;
  contactPersonFirstName?: string | null;
  contactPersonLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  currency?: string | null;
  timeZone?: string | null;
};

export type PlatformOrganizationUpdateRequest = PlatformOrganizationCreateRequest;

export type PlatformOrganizationAccessMember = {
  id: string;
  userId?: string | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  orgRole?: string | null;
  membershipType?: string | null;
  status?: string | null;
  accessScopeType?: string | null;
  invitedAt?: string | null;
  acceptedAt?: string | null;
  joinedAt?: string | null;
};

export type PlatformOrganizationAccessInviteRequest = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  orgRole: string;
  accessScopeType?: string | null;
  reason?: string | null;
};

export type PlatformOrganizationAccessUpdateRequest = {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  orgRole?: string | null;
  accessScopeType?: string | null;
  status?: string | null;
  reason?: string | null;
};

export type PlatformOrganizationAccess = {
  organizationId: string;
  organizationName?: string | null;
  members?: PlatformOrganizationAccessMember[] | null;
  totalElements?: number | null;
  totalPages?: number | null;
  page?: number | null;
  size?: number | null;
};

export type PlatformAddressSuggestion = {
  label: string;
  line1?: string | null;
  address_line1?: string | null;
  formattedAddress?: string | null;
  formatted_address?: string | null;
  city?: string | null;
  municipality?: string | null;
  state?: string | null;
  stateCode?: string | null;
  state_code?: string | null;
  postalCode?: string | null;
  postcode?: string | null;
  postal_code?: string | null;
  country?: string | null;
  countryCode?: string | null;
  country_code?: string | null;
  provider?: string | null;
  placeId?: string | null;
  place_id?: string | null;
};

export type PlatformNormalizedAddress = Omit<PlatformAddressSuggestion, 'label'>;

export type PlatformPage<T> = {
  content?: T[] | null;
  totalElements?: number | null;
  totalPages?: number | null;
  size?: number | null;
  number?: number | null;
};

export type ConvenienceFeeRefundPolicy = 'NON_REFUNDABLE_EXCEPT_PLATFORM_ERROR' | 'REFUND_WITH_RENT';

export type ConvenienceFeeBand = { upTo: number | null; fee: number };

export type ConvenienceFeeChargeStatus = 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'UNKNOWN' | 'REFUNDED';

/** One Apex convenience fee, charged on the platform account separately from the organization's rent. */
export type ConvenienceFeeCharge = {
  paymentAttemptId: string;
  orgId: string | null;
  leaseId: string | null;
  tenantId: string | null;
  rentAmount: number;
  rentStatus: string | null;
  feeAmount: number;
  feeBasis: string | null;
  currency: string;
  feeStatus: ConvenienceFeeChargeStatus;
  feePaymentId: string | null;
  failureReason: string | null;
  rentReversalRecorded: boolean;
  refundReason: string | null;
  refundedBy: string | null;
  refundedAt: string | null;
  createdAt: string | null;
};

export type ConvenienceFeeChargePage = { items: ConvenienceFeeCharge[]; page: number; size: number; total: number };

export type ConvenienceFeeSummaryRow = { status: ConvenienceFeeChargeStatus; currency: string; count: number; total: number };

export type ConvenienceFeeRefundReason = 'PLATFORM_ERROR' | 'RENT_REFUNDED';

/** Platform-wide tenant payments (rent, late fees, deposits, other property charges) by calendar month (UTC). */
export type PlatformPaymentVolume = {
  from: string;
  months: { month: string; currency: string; method: string; status: string; count: number; amount: number }[];
  topOrganizations: { orgId: string | null; currency: string; count: number; amount: number }[];
};

/** Calendar months as YYYY-MM, both included; at most 36 months and none in the future. */
export type ReportRange = { from: string; to: string };

/** Organization counts; {@code newByMonth} only lists months with new organizations. Archived ones aren't counted. */
export type PlatformOrganizationReport = {
  from: string;
  to: string;
  total: number;
  statuses: Record<string, number>;
  newByMonth: { month: string; count: number }[];
};

export type PlatformOrganizationName = { id: string; name: string | null; status: string; createdDate: string | null };

/** One subscription in a report list; {@code unitLimit} is null for unlimited plans. */
export type PlatformSubscriptionReportRow = {
  subscriptionId: string;
  ownerOrgId: string | null;
  organizationName: string | null;
  planCode: string;
  planDisplayName: string | null;
  status: string;
  billingInterval: string | null;
  amount: number | null;
  currency: string | null;
  currentPeriodEnd: string | null;
  autoRenew: boolean;
  propertiesUsed: number;
  unitsUsed: number;
  staffUsed: number;
  unitLimit: number | null;
  unitShare: number | null;
};

export type PlatformSubscriptionReportRows = { total: number; items: PlatformSubscriptionReportRow[] };
export type PlatformSubscriptionReportKind = 'FAILING' | 'RENEWALS' | 'USAGE';

/** Subscription totals worked out by the subscription service; lists hold the first few rows and the full count. */
export type PlatformSubscriptionReport = {
  statuses: Record<string, number>;
  plans: { planCode: string; planDisplayName: string; currency: string; active: number; trials: number; failing: number; monthlyRecurringRevenue: number }[];
  failing: PlatformSubscriptionReportRows;
  renewals: PlatformSubscriptionReportRows;
  largest: PlatformSubscriptionReportRows;
  usageTotals: { subscriptions: number; properties: number; units: number; staff: number };
};

/** Convenience fees by month and fee status (months in range) plus all-time totals. */
export type PlatformConvenienceFeeReport = {
  from: string;
  to: string;
  months: { month: string; currency: string; status: string; count: number; total: number }[];
  allTime: { status: string; currency: string; count: number; total: number }[];
};

export type PlatformRevenueStream = 'SUBSCRIPTIONS' | 'SCREENING_FEES' | 'CONVENIENCE_FEES';

/**
 * Money Apex collected by month and stream (gross). {@code subscriptionsRecordedSince} is when the earliest recorded
 * subscription invoice was paid; subscription revenue before then isn't in the ledger until it's imported.
 */
export type PlatformRevenue = {
  from: string;
  to: string;
  months: { month: string; currency: string; stream: PlatformRevenueStream; count: number; amount: number }[];
  subscriptionsRecordedSince: string | null;
};

export type SubscriptionInvoiceBackfillResult = { scanned: number; recorded: number; alreadyRecorded: number; notApexSubscriptions: number };

/** Screening request counts by calendar month (UTC) of the request; no applicant data. */
export type PlatformScreeningVolume = {
  from: string;
  months: { month: string; status: string; decision: string; provider: string; count: number }[];
  topOrganizations: { orgId: string; requested: number; completed: number }[];
};

/** Maintenance request counts by calendar month (UTC) the request was opened; no titles or tenant names. */
export type PlatformMaintenanceVolume = {
  from: string;
  months: { month: string; status: string; priority: string; category: string; count: number }[];
  overdueNow: number;
  topOrganizations: { orgId: string; opened: number; stillOpen: number }[];
};

export type ConvenienceFeeRetryResult = { status: ConvenienceFeeChargeStatus; outcome: string };

export type PlatformConvenienceFeeSettings = {
  enabled: boolean;
  currency: string;
  creditPercent: number;
  creditFixed: number;
  debitBands: ConvenienceFeeBand[];
  achFixed: number;
  refundPolicy: ConvenienceFeeRefundPolicy;
  statementDescriptor: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  version?: number | null;
};

export type PlatformScreeningFeeSettings = {
  enabled: boolean;
  amount: number;
  currency: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type PlatformCertificateStatus = 'OK' | 'EXPIRING_SOON' | 'EXPIRED' | 'UNREACHABLE' | 'WARNING' | 'CRITICAL';

export type PlatformCertificateHealthItem = {
  host: string;
  port: number;
  status: PlatformCertificateStatus | string;
  expiresAt?: string | null;
  daysRemaining?: number | null;
  issuer?: string | null;
  subject?: string | null;
  error?: string | null;
};

export type PlatformCertificateHealth = {
  status: PlatformCertificateStatus | string;
  warningThresholdDays: number;
  checkedAt?: string | null;
  certificates?: PlatformCertificateHealthItem[] | null;
};

export type PlatformIntegrationState = 'OK' | 'DEGRADED' | 'DOWN' | 'NOT_CONFIGURED';
export type PlatformIntegrationCheckState = 'OK' | 'WARN' | 'FAIL' | 'INFO';
/** One external provider's health, in the shape every backend service returns for the Integrations page. */
export type PlatformIntegrationStatus = {
  provider: string;
  name: string;
  purpose: string | null;
  status: PlatformIntegrationState;
  mode: 'LIVE' | 'TEST' | null;
  summary: string | null;
  checkedAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  checks: Array<{ key: string; label: string; status: PlatformIntegrationCheckState; detail: string | null }>;
  metrics: Array<{ key: string; label: string; value: number; hint: string | null }>;
  dashboardUrl: string | null;
  /** Whether the page can ask for a read-only "Run check" of the credentials. */
  liveCheckSupported?: boolean;
  liveCheckAt?: string | null;
};
/** Which backend answers for a card, so "Run check" goes to the right service. */
export type PlatformIntegrationSource = 'payments' | 'accounts' | 'screening' | 'ai';

export type PlatformSupportAccessMode = 'READ_ONLY' | 'FULL_ACCESS';

export type PlatformSupportSessionResponse = {
  id: string;
  organizationId: string;
  organizationName?: string | null;
  adminUserId?: string | null;
  adminEmail?: string | null;
  reason?: string | null;
  accessMode: PlatformSupportAccessMode;
  status: string;
  expiresAt?: string | null;
  endedAt?: string | null;
};

export type PlatformAccountDeletionRequestStatus =
  | 'PENDING'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'CANCELED';

export type PlatformAccountDeletionImpactItem = {
  code?: string | null;
  label?: string | null;
  message?: string | null;
  count?: number | null;
  actionRequired?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
};

export type PlatformAccountDeletionOrganizationImpact = {
  organizationId?: string | null;
  organizationName?: string | null;
  blockerCount?: number | null;
  retentionRecordCount?: number | null;
  blockers?: PlatformAccountDeletionImpactItem[] | null;
  retentionRecords?: PlatformAccountDeletionImpactItem[] | null;
};

export type PlatformAccountDeletionImpact = {
  userId?: string | null;
  canCompleteDeletion?: boolean | null;
  blockerCount?: number | null;
  retentionRecordCount?: number | null;
  blockers?: PlatformAccountDeletionImpactItem[] | null;
  retentionRecords?: PlatformAccountDeletionImpactItem[] | null;
  organizations?: PlatformAccountDeletionOrganizationImpact[] | null;
  checkedAt?: string | null;
};

export type PlatformAccountDeletionRequest = {
  id: string;
  userId?: string | null;
  emailSnapshot?: string | null;
  displayNameSnapshot?: string | null;
  accountType?: string | null;
  requesterType?: string | null;
  sourceApp?: string | null;
  status: PlatformAccountDeletionRequestStatus;
  reason?: string | null;
  retentionNotes?: string | null;
  adminNotes?: string | null;
  decisionReason?: string | null;
  requestedAt?: string | null;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  reviewedByEmail?: string | null;
  completedAt?: string | null;
  completedByUserId?: string | null;
  completedByEmail?: string | null;
  canceledAt?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
  version?: number | null;
  propertyImpact?: PlatformAccountDeletionImpact | null;
};

export type PlatformAccountDeletionActionRequest = {
  reason?: string | null;
  retentionNotes?: string | null;
  adminNotes?: string | null;
};

export type PlatformRoleDefinition = {
  code: string; label: string; description: string; portalAccess: boolean;
  access: string[]; authorities: string[]; assignedUsers: number;
};
export type PlatformRoleCatalog = { roles: PlatformRoleDefinition[]; canManage: boolean; actorId: string; confirmationMethod: string | null };
export type PlatformRoleHistory = { id: number; createdAt: string; actorEmail: string; targetEmail: string; beforeRoles: string[]; afterRoles: string[]; reason: string };

export type PlatformUserStatus = 'ACTIVE' | 'PENDING' | 'LOCKED' | 'DISABLED';

/** Security & Access (auth server, /v1/profile/platform/security). */
export type PlatformSecuritySummary = {
  privilegedAccounts: number;
  privilegedWithoutMfa: number;
  activeUsers: number;
  activeUsersWithMfa: number;
  signedInAdminSessions: number;
  adminSessionsActiveNow: number;
  disabledAccounts: number;
  lockedAccounts: number;
  failedSignIns24h: number;
  failedAccounts24h: number;
  topFailures24h: Array<{ email: string; attempts: number; lastAttemptAt: string; knownAccount: boolean }>;
};
export type PlatformSecuritySession = {
  id: string; userId: string; email: string | null; displayName: string | null; roles: string[];
  deviceLabel: string | null; userAgent: string | null; ip: string | null; city: string | null; region: string | null; country: string | null;
  createdAt: string; lastSeenAt: string; activeNow: boolean; privileged: boolean;
};
export type PlatformSignInFailureStage = 'PASSWORD' | 'MFA' | 'DISABLED';
export type PlatformSignInFailure = { id: number; email: string; knownAccount: boolean; stage: PlatformSignInFailureStage; ip: string | null; userAgent: string | null; createdAt: string };
export type PlatformSecurityActivityType = 'SIGN_IN' | 'PASSWORD' | 'ACCOUNT' | 'ROLES' | 'SESSIONS';
export type PlatformSecurityActivity = { id: number; createdAt: string; type: PlatformSecurityActivityType; event: string; actorEmail: string | null; subject: string | null; reason: string | null; detail: string | null };
/** One action a platform admin took, from the auth service's audit log. */
export type PlatformAuditEntry = {
  id: number;
  createdAt: string;
  event: string;
  action: string | null;
  serviceName: string | null;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  actorId: string | null;
  actorEmail: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
};
export type PlatformAuditQuery = { q?: string; event?: string; from?: string | null; to?: string | null };

export type PlatformUserSummary = {
  id: string;
  email: string;
  displayName?: string | null;
  accountType?: string | null;
  roles?: string[] | null;
  status: PlatformUserStatus;
  mfaEnabled: boolean;
  authenticatorConfigured: boolean;
  createdAt?: string | null;
};

export type PlatformUserSession = {
  id: string;
  deviceLabel?: string | null;
  browserName?: string | null;
  browserVersion?: string | null;
  osName?: string | null;
  ip?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  createdAt?: string | null;
  lastSeenAt?: string | null;
  active: boolean;
  revoked: boolean;
};

export type PlatformUserActivity = {
  id: number;
  createdAt?: string | null;
  serviceName?: string | null;
  entityType?: string | null;
  action?: string | null;
  actorEmail?: string | null;
  byThisUser: boolean;
  details?: Record<string, unknown> | null;
};

export type PlatformUserDetail = {
  user: PlatformUserSummary;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  phoneVerified: boolean;
  country?: string | null;
  timeZone?: string | null;
  accountValidated: boolean;
  adminValidated: boolean;
  lockedUntil?: string | null;
  lastSignInAt?: string | null;
  activeSessionCount: number;
  sessions?: PlatformUserSession[] | null;
  recentActivity?: PlatformUserActivity[] | null;
};

export type PlatformUserQuery = {
  page?: number;
  size?: number;
  q?: string | null;
  status?: PlatformUserStatus | 'ALL' | null;
  accountType?: string | null;
  role?: string | null;
  mfaEnabled?: boolean | null;
};

export type PlatformInvitationType = 'STAFF' | 'TENANT' | 'OWNER' | 'APPLICATION';
export type PlatformInvitationStatus = 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'DECLINED' | 'REVOKED';

export type PlatformInvitation = {
  type: PlatformInvitationType;
  id: string;
  organizationId: string;
  organizationName?: string | null;
  email?: string | null;
  inviteeName?: string | null;
  role?: string | null;
  invitedByUserId?: string | null;
  invitedByEmail?: string | null;
  sentAt?: string | null;
  expiresAt?: string | null;
  respondedAt?: string | null;
  status: PlatformInvitationStatus;
  rawStatus?: string | null;
  canResend: boolean;
  canRevoke: boolean;
};

export type PlatformInvitationPage = {
  content: PlatformInvitation[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
};

export type PlatformInvitationSummary = { pending: number; expiringSoon: number; expired: number; accepted: number };

export type PlatformInvitationQuery = {
  page?: number;
  size?: number;
  q?: string | null;
  type?: PlatformInvitationType | 'ALL' | null;
  status?: PlatformInvitationStatus | 'ALL' | null;
};

export type PlatformPaymentStatus = 'PROCESSING' | 'REQUIRES_REVIEW' | 'SUCCEEDED' | 'FAILED';
export type PlatformPaymentStatusFilter = PlatformPaymentStatus | 'NEEDS_REVIEW' | 'ALL';

export type PlatformPayment = {
  id: string;
  orgId?: string | null;
  leaseId?: string | null;
  userId?: string | null;
  type?: string | null;
  method?: string | null;
  status: PlatformPaymentStatus;
  needsReview: boolean;
  reversalReviewStatus?: string | null;
  amount: number;
  currency?: string | null;
  platformFeeAmount?: number | null;
  tenantPlatformFeeAmount?: number | null;
  tenantProcessingFeeAmount?: number | null;
  convenienceFeeAmount?: number | null;
  convenienceFeeStatus?: string | null;
  provider?: string | null;
  providerPaymentId?: string | null;
  liveMode?: boolean | null;
  planCode?: string | null;
  description?: string | null;
  failureReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type PlatformPaymentPage = { content: PlatformPayment[]; totalElements: number; totalPages: number; page: number; size: number };

export type PlatformPaymentSummary = {
  from?: string | null;
  to?: string | null;
  succeeded: Array<{ currency: string | null; count: number; amount: number; platformFees: number }>;
  failed: number;
  needsReview: number;
  processing: number;
};

export type PlatformPaymentQuery = {
  page?: number;
  size?: number;
  q?: string | null;
  status?: PlatformPaymentStatusFilter | null;
  type?: string | null;
  method?: string | null;
  orgId?: string | null;
  from?: string | null;
  to?: string | null;
};

export type PlatformSubscriptionPlan = {
  id: string;
  planCode: string;
  accountType: string;
  displayName: string;
  description?: string | null;
  billingInterval: string;
  priceAmount: number | null;
  currency: string;
  features?: string[] | null;
  maxOrganizations: number;
  maxPropertiesPerOrg: number;
  maxStaffPerOrg: number;
  maxUnitsPerOrg: number;
  monthlySmsLimit?: number;
  unlimitedOrganizations: boolean;
  unlimitedProperties?: boolean | null;
  unlimitedStaff?: boolean | null;
  unlimitedUnits?: boolean | null;
  recommended?: boolean | null;
  active?: boolean | null;
  trialEnabled?: boolean | null;
  trialDays?: number | null;
  cardRequiredForTrial?: boolean | null;
  externalProvider?: string | null;
  externalProductId?: string | null;
  externalPriceId?: string | null;
  sortOrder?: number | null;
  version?: number | null;
};

export type PlatformSubscriptionPlanInput = Omit<PlatformSubscriptionPlan, 'id'>;

export type PlatformUserProfile = {
  userId: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
};

export type PlatformSubscription = {
  subscriptionId: string;
  ownerUserId?: string | null;
  ownerOrgId?: string | null;
  ownerType?: string | null;
  organizationName?: string | null;
  planCode: string;
  planDisplayName?: string | null;
  status: string;
  billingInterval?: string | null;
  startsAt?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  canceledAt?: string | null;
  autoRenew?: boolean | null;
  amount?: number | null;
  currency?: string | null;
  organizationsUsed?: number | null;
  staffUsed?: number | null;
  propertiesUsed?: number | null;
  unitsUsed?: number | null;
  /** The subscriber's own plan-feature setting. */
  featureEnforcement?: SubscriberEnforcement | null;
  /** Whether they get only their plan's features (plus extras) under the current platform mode. */
  featuresEnforced?: boolean | null;
  /** Features granted beyond the plan. */
  extraFeatures?: string[] | null;
  version?: number | null;
};

/** OFF: no one; PILOT: only subscribers marked ENFORCE; ON: everyone except EXEMPT. Limits are always enforced. */
export type FeatureEnforcementMode = 'OFF' | 'PILOT' | 'ON';
export type SubscriberEnforcement = 'INHERIT' | 'ENFORCE' | 'EXEMPT';

export type SubscriberEnforcementSettings = {
  ownerUserId: string;
  enforcement: SubscriberEnforcement;
  extraFeatures: string[];
  featuresEnforced: boolean;
  reason?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
};

export type FeatureEnforcementSettings = {
  mode: FeatureEnforcementMode;
  /** False until the mode is first saved; until then defaultMode (from the server's environment) applies. */
  saved: boolean;
  defaultMode: FeatureEnforcementMode;
  reason?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  subscribers: SubscriberEnforcementSettings[];
};

export type FeatureEnforcementChange = {
  ownerUserId?: string | null;
  previousValue?: string | null;
  newValue: string;
  reason: string;
  changedBy?: string | null;
  changedAt: string;
};

@Injectable({ providedIn: 'root' })
export class PlatformAdminPortalService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiGatewayUrl;
  private readonly propertyBase = `${environment.apiGatewayUrl}/property-service`;
  private readonly paymentsBase = `${environment.apiGatewayUrl}/payments/api/v1`;
  private readonly maintenanceBase = `${environment.apiGatewayUrl}/maintenance-service`;
  private readonly recentPlatformAdminLoginKey = 'apexPlatformAdminRecentLoginAt';
  private readonly cookieSettleDelayMs = 750;
  private refreshInFlight$: Observable<unknown> | null = null;
  private verifySessionInFlight$: Observable<PlatformAdminSession> | null = null;

  verifySession(): Observable<PlatformAdminSession> {
    if (this.verifySessionInFlight$) {
      return this.verifySessionInFlight$;
    }

    const authContext = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    const request = () => this.http.get<PlatformAdminSession>(`${this.base}/api/v1/user/auth-verify`, {
      context: authContext,
      observe: 'response',
      withCredentials: true,
    }).pipe(
      tap((response) => this.logAuthHeaders('auth-verify', response)),
      map((response) => response.body ?? this.unauthenticatedSession()),
    );

    this.verifySessionInFlight$ = request().pipe(
      tap((session) => {
        if (!session?.authenticated) {
          this.verifySessionInFlight$ = null;
        }
      }),
      catchError((error: unknown) => {
        if (this.isMissingCookieAuthVerify(error)) {
          return of(this.unauthenticatedSession());
        }

        if (!this.isAuthExpired(error)) {
          this.verifySessionInFlight$ = null;
          return throwError(() => error);
        }

        return this.refreshSession().pipe(
          switchMap(() => timer(this.cookieSettleDelayMs)),
          switchMap(() => request()),
          catchError(() => of(this.unauthenticatedSession())),
        );
      }),
      timeout(15000),
      finalize(() => {
        this.verifySessionInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.verifySessionInFlight$;
  }

  async settleAuthenticatedSession(): Promise<PlatformAdminSession | null> {
    const delaysMs = [this.cookieSettleDelayMs, 750, 1250, 2000];

    for (const delayMs of delaysMs) {
      await this.wait(delayMs);
      this.verifySessionInFlight$ = null;

      const session = await new Promise<PlatformAdminSession | null>((resolve) => {
        this.verifySession().pipe(timeout(8000)).subscribe({
          next: (value) => resolve(value),
          error: () => resolve(null),
        });
      });

      if (session?.authenticated) {
        return session;
      }
    }

    return null;
  }

  preAuthenticate(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Observable<PlatformAdminPreAuthResponse> {
    return this.http.post<PlatformAdminPreAuthResponse>(`${this.base}/api/v1/user/pre-authenticate`, payload, {
      withCredentials: true,
    });
  }

  login(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Observable<unknown> {
    this.clearSessionVerification();

    const body = new URLSearchParams();
    body.set('username', payload.email);
    body.set('password', payload.password);
    body.set('grant_type', 'password');
    body.set('rememberMe', String(payload.rememberMe === true));

    return this.http.post(`${this.base}/api/v1/user/login`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      observe: 'response',
      withCredentials: true,
    }).pipe(
      tap((response) => this.logAuthHeaders('login', response)),
      map((response) => response.body),
    );
  }

  getMfaDeliveryCapabilities(payload: { userId: string; preAuthToken: string; purpose: 'LOGIN' }) {
    return this.http.post<MfaDeliveryCapabilities>(
      `${this.base}/api/v1/user/mfa/delivery-capabilities`, payload, {
        withCredentials: true,
        context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
      },
    ).pipe(
      timeout(8000),
      map((response) => ({
        smsAvailable: response?.smsAvailable === true,
        smsDestination: response?.smsAvailable === true && typeof response.smsDestination === 'string'
          ? response.smsDestination : null,
      })),
      catchError(() => of({ smsAvailable: false, smsDestination: null })),
    );
  }

  initiateEmailMfa(payload: {
    challengeId?: string | null;
    userId?: string | null;
  }): Observable<PlatformAdminMfaInitiateResponse> {
    return this.initiateMfa({ ...payload, channel: 'EMAIL' });
  }

  initiateMfa(payload: { preAuthToken?: string | null;
    channel: PlatformAdminMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
    email?: string | null;
  }): Observable<PlatformAdminMfaInitiateResponse> {
    const body: Record<string, unknown> = {
      purpose: 'LOGIN',
      channel: payload.channel,
    };

    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }
    if (payload.email) {
      body['email'] = payload.email;
    }

    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<PlatformAdminMfaInitiateResponse>(`${this.base}/api/v1/user/send-token`, body, {
      withCredentials: true,
    }).pipe(
      map((response) => {
        const confirmed = response?.statusCode === 200
          || (response?.statusCode === undefined && response?.sent === true);
        if (!confirmed || response?.sent === false) {
          throw new Error(response?.message || 'We could not send your security code.');
        }
        return { ...response, sent: true, channel: response.channel ?? payload.channel };
      }),
    );
  }

  verifyEmailMfa(payload: {
    otpCode: string;
    challengeId?: string | null;
    userId?: string | null;
    email?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }): Observable<PlatformAdminVerifyMfaResponse> {
    return this.verifyMfa({ ...payload, channel: 'EMAIL' });
  }

  verifyMfa(payload: {
    otpCode: string;
    channel: PlatformAdminMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
    email?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }): Observable<PlatformAdminVerifyMfaResponse> {
    this.clearSessionVerification();

    const body: Record<string, unknown> = {
      otpCode: payload.otpCode,
      purpose: 'LOGIN',
      channel: payload.channel,
      rememberMe: payload.rememberMe === true,
    };

    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }
    if (payload.email) {
      body['email'] = payload.email;
    }
    // Proves this code belongs to the password step that started the login.
    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<PlatformAdminVerifyMfaResponse>(`${this.base}/api/v1/user/verify`, body, {
      observe: 'response',
      withCredentials: true,
    }).pipe(
      tap((response) => this.logAuthHeaders('mfa-verify', response)),
      map((response) => response.body ?? {
        statusCode: response.status,
        message: response.statusText,
      }),
    );
  }

  markPlatformAdminLoginVerified(): void {
    this.clearSessionVerification();

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(this.recentPlatformAdminLoginKey, String(Date.now()));
    } catch {
      // Storage is best-effort only.
    }
  }

  hasRecentPlatformAdminLoginGrace(maxAgeMs = 120000): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const timestamp = Number(window.sessionStorage.getItem(this.recentPlatformAdminLoginKey) || '0');
      return Number.isFinite(timestamp) && timestamp > 0 && Date.now() - timestamp <= maxAgeMs;
    } catch {
      return false;
    }
  }

  getOrganizationContext(): Observable<OrganizationContextResponse> {
    return this.http.get<OrganizationContextResponse>(`${this.propertyBase}/organization/context`, {
      withCredentials: true,
    });
  }

  getOrganizations(params: {
    page?: number;
    size?: number;
    q?: string | null;
    status?: string | null;
    sort?: string | null;
  } = {}): Observable<PlatformPage<PlatformOrganization> & { summary?: Record<string, number> }> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 0,
      size: params.size ?? 100,
      sort: params.sort || 'name,asc',
      deleted: params.status === 'ARCHIVED',
    };

    if (params.q?.trim()) {
      query['q'] = params.q.trim();
    }
    if (params.status && !['ALL', 'ARCHIVED'].includes(params.status)) {
      query['status'] = params.status;
    }

    return this.http.get<PlatformPage<PlatformOrganization> & { summary?: Record<string, number> }>(`${this.propertyBase}/platform/organizations/browse`, {
      params: query,
      withCredentials: true,
    });
  }

  createOrganization(payload: PlatformOrganizationCreateRequest): Observable<PlatformOrganization> {
    return this.http.post<PlatformOrganization>(`${this.propertyBase}/platform/organizations`, payload, {
      withCredentials: true,
    });
  }

  updateOrganization(organizationId: string, payload: PlatformOrganizationUpdateRequest): Observable<PlatformOrganization> {
    return this.http.patch<PlatformOrganization>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}`,
      payload,
      { withCredentials: true },
    );
  }

  deactivateOrganization(organizationId: string, reason?: string | null): Observable<PlatformOrganization> {
    return this.http.post<PlatformOrganization>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/deactivate`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  reactivateOrganization(organizationId: string, reason?: string | null): Observable<PlatformOrganization> {
    return this.http.post<PlatformOrganization>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/reactivate`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  restoreOrganization(organizationId: string, reason?: string | null): Observable<PlatformOrganization> {
    return this.http.post<PlatformOrganization>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/restore`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  archiveOrganization(organizationId: string, reason?: string | null): Observable<PlatformOrganization> {
    return this.http.delete<PlatformOrganization>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}`,
      {
        body: { reason: reason?.trim() || null },
        withCredentials: true,
      },
    );
  }

  getOrganizationAccess(organizationId: string, params: { page?: number; size?: number; q?: string | null } = {}): Observable<PlatformOrganizationAccess> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 10,
    };
    if (params.q?.trim()) {
      query['q'] = params.q.trim();
    }

    return this.http.get<PlatformOrganizationAccess>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access`,
      {
        params: query,
        withCredentials: true,
      },
    );
  }

  inviteOrganizationAccess(
    organizationId: string,
    payload: PlatformOrganizationAccessInviteRequest,
  ): Observable<PlatformOrganizationAccessMember> {
    return this.http.post<PlatformOrganizationAccessMember>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access`,
      payload,
      { withCredentials: true },
    );
  }

  updateOrganizationAccess(
    organizationId: string,
    membershipId: string,
    payload: PlatformOrganizationAccessUpdateRequest,
  ): Observable<PlatformOrganizationAccessMember> {
    return this.http.patch<PlatformOrganizationAccessMember>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access/${encodeURIComponent(membershipId)}`,
      payload,
      { withCredentials: true },
    );
  }

  resendOrganizationAccessInvite(
    organizationId: string,
    membershipId: string,
    reason?: string | null,
  ): Observable<PlatformOrganizationAccessMember> {
    return this.http.post<PlatformOrganizationAccessMember>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access/${encodeURIComponent(membershipId)}/resend`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  revokeOrganizationAccess(
    organizationId: string,
    membershipId: string,
    reason?: string | null,
  ): Observable<PlatformOrganizationAccessMember> {
    return this.http.post<PlatformOrganizationAccessMember>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access/${encodeURIComponent(membershipId)}/revoke`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  reactivateOrganizationAccess(
    organizationId: string,
    membershipId: string,
    reason?: string | null,
  ): Observable<PlatformOrganizationAccessMember> {
    return this.http.post<PlatformOrganizationAccessMember>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/access/${encodeURIComponent(membershipId)}/reactivate`,
      { reason: reason?.trim() || null },
      { withCredentials: true },
    );
  }

  autocompleteAddress(text: string, countries = 'us,ca'): Observable<PlatformAddressSuggestion[]> {
    return this.http.get<PlatformAddressSuggestion[]>(`${this.propertyBase}/address-lookup/autocomplete`, {
      params: { text, countries },
      withCredentials: true,
    }).pipe(map((suggestions) => suggestions.map((suggestion) => this.normalizeAddressSuggestion(suggestion))));
  }

  resolveAddress(query: string, placeId?: string | null, countries = 'us,ca'): Observable<PlatformNormalizedAddress> {
    const params: Record<string, string> = { query, countries };
    if (placeId) {
      params['placeId'] = placeId;
    }

    return this.http.get<PlatformNormalizedAddress>(`${this.propertyBase}/address-lookup/resolve`, {
      params,
      withCredentials: true,
    }).pipe(map((address) => this.normalizeAddress(address)));
  }

  getConvenienceFeeSettings(): Observable<PlatformConvenienceFeeSettings> {
    return this.http.get<PlatformConvenienceFeeSettings>(`${this.paymentsBase}/platform/convenience-fee-settings`, {
      withCredentials: true,
    });
  }

  getConvenienceFees(status: ConvenienceFeeChargeStatus | null, page = 0, size = 25): Observable<ConvenienceFeeChargePage> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<ConvenienceFeeChargePage>(`${this.paymentsBase}/platform/convenience-fees`, { params, withCredentials: true });
  }

  getConvenienceFeeSummary(): Observable<ConvenienceFeeSummaryRow[]> {
    return this.http.get<ConvenienceFeeSummaryRow[]>(`${this.paymentsBase}/platform/convenience-fees/summary`, { withCredentials: true });
  }

  /** Every organization, or just {@code orgId}, for {@code range}. The same applies to the screening and maintenance totals. */
  getPaymentVolume(range: ReportRange, orgId: string | null = null): Observable<PlatformPaymentVolume> {
    return this.http.get<PlatformPaymentVolume>(`${this.paymentsBase}/platform/reports/payment-volume`, {
      params: reportParams(range, orgId), withCredentials: true,
    });
  }

  getOrganizationReport(range: ReportRange): Observable<PlatformOrganizationReport> {
    return this.http.get<PlatformOrganizationReport>(`${this.propertyBase}/platform/reports/organizations`, {
      params: reportParams(range, null), withCredentials: true,
    });
  }

  /** Names for up to 200 organizations, for the lists a report shows. */
  getOrganizationNames(ids: string[]): Observable<PlatformOrganizationName[]> {
    return this.http.get<PlatformOrganizationName[]>(`${this.propertyBase}/platform/reports/organizations/names`, {
      params: { ids: ids.join(',') }, withCredentials: true,
    });
  }

  getSubscriptionReport(orgId: string | null = null): Observable<PlatformSubscriptionReport> {
    return this.http.get<PlatformSubscriptionReport>(`${this.base}/subscriptions/api/v1/internal/subscriptions/admin/report`, {
      params: orgId ? { orgId } : {}, withCredentials: true,
    });
  }

  getSubscriptionReportRows(kind: PlatformSubscriptionReportKind, orgId: string | null, page: number, size: number): Observable<PlatformSubscriptionReportRows> {
    let params = new HttpParams().set('kind', kind).set('page', page).set('size', size);
    if (orgId) params = params.set('orgId', orgId);
    return this.http.get<PlatformSubscriptionReportRows>(`${this.base}/subscriptions/api/v1/internal/subscriptions/admin/report/rows`, {
      params, withCredentials: true,
    });
  }

  getConvenienceFeeReport(range: ReportRange, orgId: string | null = null): Observable<PlatformConvenienceFeeReport> {
    return this.http.get<PlatformConvenienceFeeReport>(`${this.paymentsBase}/platform/reports/convenience-fees`, {
      params: reportParams(range, orgId), withCredentials: true,
    });
  }

  getPlatformRevenue(range: ReportRange, orgId: string | null = null): Observable<PlatformRevenue> {
    return this.http.get<PlatformRevenue>(`${this.paymentsBase}/platform/reports/platform-revenue`, {
      params: reportParams(range, orgId), withCredentials: true,
    });
  }

  /** Imports subscription invoices paid since {@code since} (YYYY-MM-DD) from Stripe; safe to repeat. */
  backfillSubscriptionInvoices(since: string): Observable<SubscriptionInvoiceBackfillResult> {
    return this.http.post<SubscriptionInvoiceBackfillResult>(`${this.paymentsBase}/platform/reports/subscription-invoices/backfill`, null, {
      params: { since }, withCredentials: true,
    });
  }

  getScreeningVolume(range: ReportRange, orgId: string | null = null): Observable<PlatformScreeningVolume> {
    return this.http.get<PlatformScreeningVolume>(`${this.propertyBase}/platform/reports/screening`, {
      params: reportParams(range, orgId), withCredentials: true,
    });
  }

  getMaintenanceVolume(range: ReportRange, orgId: string | null = null): Observable<PlatformMaintenanceVolume> {
    return this.http.get<PlatformMaintenanceVolume>(`${this.maintenanceBase}/platform/reports/maintenance`, {
      params: reportParams(range, orgId), withCredentials: true,
    });
  }

  /** Charges a failed or unknown fee again; the server checks Stripe first so it's never charged twice. */
  retryConvenienceFee(paymentAttemptId: string): Observable<ConvenienceFeeRetryResult> {
    return this.http.post<ConvenienceFeeRetryResult>(
      `${this.paymentsBase}/platform/convenience-fees/${encodeURIComponent(paymentAttemptId)}/retry`, {}, { withCredentials: true });
  }

  refundConvenienceFee(paymentAttemptId: string, reason: ConvenienceFeeRefundReason, note: string): Observable<ConvenienceFeeCharge> {
    return this.http.post<ConvenienceFeeCharge>(
      `${this.paymentsBase}/platform/convenience-fees/${encodeURIComponent(paymentAttemptId)}/refund`,
      { reason, note }, { withCredentials: true });
  }

  updateConvenienceFeeSettings(payload: Omit<PlatformConvenienceFeeSettings, 'updatedBy' | 'updatedAt'>): Observable<PlatformConvenienceFeeSettings> {
    return this.http.put<PlatformConvenienceFeeSettings>(`${this.paymentsBase}/platform/convenience-fee-settings`, payload, {
      withCredentials: true,
    });
  }

  getScreeningFeeSettings(): Observable<PlatformScreeningFeeSettings> {
    return this.http.get<PlatformScreeningFeeSettings>(`${this.propertyBase}/platform/screening-fee-settings`, {
      withCredentials: true,
    });
  }

  /** Stripe's health, from the payment gateway. */
  getPaymentIntegrations(): Observable<PlatformIntegrationStatus[]> {
    return this.http.get<PlatformIntegrationStatus[]>(`${this.paymentsBase}/platform/integrations`, { withCredentials: true });
  }

  /** Didit, email and SMS health, from the auth server (which asks the notification service for email and SMS). */
  getAccountIntegrations(): Observable<PlatformIntegrationStatus[]> {
    return this.http.get<PlatformIntegrationStatus[]>(`${this.base}/api/v1/profile/platform/integrations`, { withCredentials: true });
  }

  /** Experian's health, from the screening service. */
  getScreeningIntegrations(): Observable<PlatformIntegrationStatus[]> {
    return this.http.get<PlatformIntegrationStatus[]>(`${this.base}/screening-service/api/v1/platform/integrations`, { withCredentials: true });
  }

  /** The AI provider's health, from the AI service. */
  getAiIntegrations(): Observable<PlatformIntegrationStatus[]> {
    return this.http.get<PlatformIntegrationStatus[]>(`${this.base}/ai-service/api/v1/platform/integrations`, { withCredentials: true });
  }

  /** One read-only call to the provider; answers with the provider's updated card, or 429 when checked a moment ago. */
  runIntegrationCheck(source: PlatformIntegrationSource, provider: string): Observable<PlatformIntegrationStatus> {
    const base = {
      payments: `${this.paymentsBase}/platform/integrations`,
      accounts: `${this.base}/api/v1/profile/platform/integrations`,
      screening: `${this.base}/screening-service/api/v1/platform/integrations`,
      ai: `${this.base}/ai-service/api/v1/platform/integrations`,
    }[source];
    return this.http.post<PlatformIntegrationStatus>(`${base}/${encodeURIComponent(provider)}/check`, {}, { withCredentials: true });
  }

  getCertificateHealth(): Observable<PlatformCertificateHealth> {
    return this.http.get<PlatformCertificateHealth>(`${this.propertyBase}/platform/certificates`, {
      withCredentials: true,
    });
  }

  createSupportSession(
    organizationId: string,
    payload: { reason: string; accessMode?: PlatformSupportAccessMode },
  ): Observable<PlatformSupportSessionResponse> {
    return this.http.post<PlatformSupportSessionResponse>(
      `${this.propertyBase}/platform/organizations/${encodeURIComponent(organizationId)}/support-sessions`,
      payload,
      { withCredentials: true },
    );
  }

  getSubscriptionPlans(accountType = 'LANDLORD'): Observable<PlatformSubscriptionPlan[]> {
    return this.http.get<PlatformSubscriptionPlan[]>(`${this.base}/subscriptions/api/v1/public/plans`, {
      params: { accountType },
    });
  }

  /**
   * One page of every subscription, newest first, filtered on the server. Use this for lists: unlike
   * {@link getPlatformSubscriptions} it isn't limited to the newest 200.
   */
  getPlatformSubscriptionsPage(query: { statuses?: string[]; planCode?: string | null; q?: string | null; page: number; size: number }):
    Observable<{ items: PlatformSubscription[]; total: number; page: number; size: number }> {
    let params = new HttpParams().set('page', query.page).set('size', query.size);
    for (const status of query.statuses ?? []) params = params.append('status', status);
    if (query.planCode) params = params.set('planCode', query.planCode);
    if (query.q?.trim()) params = params.set('q', query.q.trim());
    return this.http.get<{ items: PlatformSubscription[]; total: number; page: number; size: number }>(
      `${this.base}/subscriptions/api/v1/internal/subscriptions/admin/page`, { params, withCredentials: true });
  }

  /** Names and emails for a set of user ids (auth profiles; platform admins may read any). Unknown ids are left out. */
  getUserProfilesByIds(userIds: string[]): Observable<Record<string, PlatformUserProfile>> {
    return this.http.post<Record<string, PlatformUserProfile>>(`${this.base}/api/v1/profile/ids`, userIds, { withCredentials: true });
  }

  /** The newest 200 subscriptions only; lists and totals should use the paged list and the subscription report. */
  getPlatformSubscriptions(): Observable<PlatformSubscription[]> {
    return this.http.get<PlatformSubscription[]>(`${this.base}/subscriptions/api/v1/internal/subscriptions/admin`, {
      withCredentials: true,
    });
  }

  getFeatureEnforcement(): Observable<FeatureEnforcementSettings> {
    return this.http.get<FeatureEnforcementSettings>(`${this.base}/subscriptions/api/v1/internal/feature-enforcement`, {
      withCredentials: true,
    });
  }

  setFeatureEnforcementMode(mode: FeatureEnforcementMode, reason: string): Observable<FeatureEnforcementSettings> {
    return this.http.put<FeatureEnforcementSettings>(`${this.base}/subscriptions/api/v1/internal/feature-enforcement`, { mode, reason }, {
      withCredentials: true,
    });
  }

  setSubscriberEnforcement(
    ownerUserId: string,
    settings: { enforcement: SubscriberEnforcement; extraFeatures: string[]; reason: string },
  ): Observable<SubscriberEnforcementSettings> {
    return this.http.put<SubscriberEnforcementSettings>(
      `${this.base}/subscriptions/api/v1/internal/feature-enforcement/subscribers/${encodeURIComponent(ownerUserId)}`,
      settings,
      { withCredentials: true },
    );
  }

  getFeatureEnforcementChanges(limit = 50): Observable<FeatureEnforcementChange[]> {
    return this.http.get<FeatureEnforcementChange[]>(`${this.base}/subscriptions/api/v1/internal/feature-enforcement/changes`, {
      params: { limit }, withCredentials: true,
    });
  }

  getAdminSubscriptionPlans(): Observable<PlatformSubscriptionPlan[]> {
    return this.http.get<PlatformSubscriptionPlan[]>(`${this.base}/subscriptions/api/v1/internal/subscription-plans`, {
      params: { includeInactive: true }, withCredentials: true,
    });
  }

  createSubscriptionPlan(plan: PlatformSubscriptionPlanInput): Observable<PlatformSubscriptionPlan> {
    return this.http.post<PlatformSubscriptionPlan>(`${this.base}/subscriptions/api/v1/internal/subscription-plans`, plan, {
      withCredentials: true,
    });
  }

  updateSubscriptionPlan(planCode: string, plan: PlatformSubscriptionPlanInput): Observable<PlatformSubscriptionPlan> {
    const { planCode: ignored, ...changes } = plan;
    return this.http.patch<PlatformSubscriptionPlan>(`${this.base}/subscriptions/api/v1/internal/subscription-plans/${encodeURIComponent(planCode)}`, changes, {
      withCredentials: true,
    });
  }

  updateScreeningFeeSettings(payload: {
    enabled: boolean;
    amount: number;
    currency: string;
  }): Observable<PlatformScreeningFeeSettings> {
    return this.http.put<PlatformScreeningFeeSettings>(`${this.propertyBase}/platform/screening-fee-settings`, payload, {
      withCredentials: true,
    });
  }

  getAccountDeletionRequests(params: {
    page?: number;
    size?: number;
    q?: string | null;
    status?: PlatformAccountDeletionRequestStatus | 'ALL' | null;
  } = {}): Observable<PlatformPage<PlatformAccountDeletionRequest>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 25,
    };
    if (params.q?.trim()) {
      query['q'] = params.q.trim();
    }
    if (params.status && params.status !== 'ALL') {
      query['status'] = params.status;
    }

    return this.http.get<PlatformPage<PlatformAccountDeletionRequest>>(
      `${this.base}/api/v1/account-deletion-requests/platform`,
      {
        params: query,
        withCredentials: true,
      },
    );
  }

  getAccountDeletionRequest(requestId: string): Observable<PlatformAccountDeletionRequest> {
    return this.http.get<PlatformAccountDeletionRequest>(
      `${this.base}/api/v1/account-deletion-requests/platform/${encodeURIComponent(requestId)}`,
      { withCredentials: true },
    );
  }

  reviewAccountDeletionRequest(
    requestId: string,
    payload: PlatformAccountDeletionActionRequest = {},
  ): Observable<PlatformAccountDeletionRequest> {
    return this.accountDeletionAction(requestId, 'review', payload);
  }

  approveAccountDeletionRequest(
    requestId: string,
    payload: PlatformAccountDeletionActionRequest = {},
  ): Observable<PlatformAccountDeletionRequest> {
    return this.accountDeletionAction(requestId, 'approve', payload);
  }

  rejectAccountDeletionRequest(
    requestId: string,
    payload: PlatformAccountDeletionActionRequest = {},
  ): Observable<PlatformAccountDeletionRequest> {
    return this.accountDeletionAction(requestId, 'reject', payload);
  }

  completeAccountDeletionRequest(
    requestId: string,
    payload: PlatformAccountDeletionActionRequest = {},
  ): Observable<PlatformAccountDeletionRequest> {
    return this.accountDeletionAction(requestId, 'complete', payload);
  }

  getPlatformUsers(params: PlatformUserQuery = {}): Observable<PlatformPage<PlatformUserSummary>> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 0,
      size: params.size ?? 25,
    };
    if (params.q?.trim()) query['q'] = params.q.trim();
    if (params.status && params.status !== 'ALL') query['status'] = params.status;
    if (params.accountType && params.accountType !== 'ALL') query['accountType'] = params.accountType;
    if (params.role && params.role !== 'ALL') query['role'] = params.role;
    if (params.mfaEnabled !== null && params.mfaEnabled !== undefined) query['mfaEnabled'] = params.mfaEnabled;

    return this.http.get<PlatformPage<PlatformUserSummary>>(`${this.base}/api/v1/profile/platform/users`, {
      params: query,
      withCredentials: true,
    });
  }

  getPlatformSecuritySummary(): Observable<PlatformSecuritySummary> {
    return this.http.get<PlatformSecuritySummary>(`${this.base}/api/v1/profile/platform/security/summary`, { withCredentials: true });
  }

  getPlatformSecuritySessions(query: { q?: string; scope: 'PRIVILEGED' | 'ALL'; page: number; size: number }): Observable<PlatformPage<PlatformSecuritySession>> {
    return this.http.get<PlatformPage<PlatformSecuritySession>>(`${this.base}/api/v1/profile/platform/security/sessions`, {
      params: { ...query, q: query.q?.trim() ?? '' }, withCredentials: true,
    });
  }

  revokePlatformSession(sessionId: string, reason: string): Observable<{ revokedSessions: number }> {
    return this.http.post<{ revokedSessions: number }>(`${this.base}/api/v1/profile/platform/security/sessions/${encodeURIComponent(sessionId)}/revoke`,
      { reason }, { withCredentials: true });
  }

  signOutPlatformUserEverywhere(userId: string, reason: string): Observable<{ revokedSessions: number }> {
    return this.http.post<{ revokedSessions: number }>(`${this.base}/api/v1/profile/platform/security/users/${encodeURIComponent(userId)}/sign-out`,
      { reason }, { withCredentials: true });
  }

  getPlatformSignInFailures(query: { q?: string; stage?: string; from?: string | null; page: number; size: number }): Observable<PlatformPage<PlatformSignInFailure>> {
    let params = new HttpParams().set('page', query.page).set('size', query.size);
    if (query.q?.trim()) params = params.set('q', query.q.trim());
    if (query.stage && query.stage !== 'ALL') params = params.set('stage', query.stage);
    if (query.from) params = params.set('from', query.from);
    return this.http.get<PlatformPage<PlatformSignInFailure>>(`${this.base}/api/v1/profile/platform/security/failed-sign-ins`, { params, withCredentials: true });
  }

  getPlatformSecurityActivity(query: { q?: string; type?: string; from?: string | null; page: number; size: number }): Observable<PlatformPage<PlatformSecurityActivity>> {
    let params = new HttpParams().set('page', query.page).set('size', query.size);
    if (query.q?.trim()) params = params.set('q', query.q.trim());
    if (query.type && query.type !== 'ALL') params = params.set('type', query.type);
    if (query.from) params = params.set('from', query.from);
    return this.http.get<PlatformPage<PlatformSecurityActivity>>(`${this.base}/api/v1/profile/platform/security/activity`, { params, withCredentials: true });
  }

  getPlatformAuditLog(query: PlatformAuditQuery & { page: number; size: number }): Observable<PlatformPage<PlatformAuditEntry>> {
    const params = auditParams(query).set('page', query.page).set('size', query.size);
    return this.http.get<PlatformPage<PlatformAuditEntry>>(`${this.base}/api/v1/audit/platform`, { params, withCredentials: true });
  }

  /** Event names recorded so far, for the Audit Logs filter. */
  getPlatformAuditEvents(): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/api/v1/audit/platform/events`, { withCredentials: true });
  }

  /** The filtered log as CSV (newest 10,000 entries). The export is itself recorded in the log. */
  exportPlatformAuditLog(query: PlatformAuditQuery): Observable<string> {
    return this.http.get(`${this.base}/api/v1/audit/platform/export`, { params: auditParams(query), responseType: 'text', withCredentials: true });
  }

  getPlatformRoleCatalog(): Observable<PlatformRoleCatalog> {
    return this.http.get<PlatformRoleCatalog>(`${this.base}/api/v1/profile/platform/roles`, { withCredentials: true });
  }

  getPlatformRoleMembers(query: { q: string; role: string; status: string; page: number; size: number; mfaEnabled?: boolean }): Observable<PlatformPage<PlatformUserSummary>> {
    return this.http.get<PlatformPage<PlatformUserSummary>>(`${this.base}/api/v1/profile/platform/roles/members`, { params: query, withCredentials: true });
  }

  getPlatformRoleHistory(page: number, size: number): Observable<PlatformPage<PlatformRoleHistory>> {
    return this.http.get<PlatformPage<PlatformRoleHistory>>(`${this.base}/api/v1/profile/platform/roles/history`, { params: { page, size }, withCredentials: true });
  }

  changePlatformRoles(userId: string, request: { roles: string[]; expectedRoles: string[]; reason: string; password?: string; code?: string }): Observable<{ roles: string[]; revokedSessions: number }> {
    return this.http.post<{ roles: string[]; revokedSessions: number }>(`${this.base}/api/v1/profile/platform/roles/members/${encodeURIComponent(userId)}`, request, { withCredentials: true, context: new HttpContext().set(SKIP_AUTH_REFRESH, true) });
  }

  getPlatformUser(userId: string): Observable<PlatformUserDetail> {
    return this.http.get<PlatformUserDetail>(`${this.base}/api/v1/profile/platform/users/${encodeURIComponent(userId)}`, {
      withCredentials: true,
    });
  }

  setPlatformUserDisabled(userId: string, disabled: boolean, reason: string): Observable<PlatformUserDetail> {
    return this.http.post<PlatformUserDetail>(
      `${this.base}/api/v1/profile/platform/users/${encodeURIComponent(userId)}/${disabled ? 'disable' : 'enable'}`,
      { reason },
      { withCredentials: true },
    );
  }

  getPlatformInvitations(params: PlatformInvitationQuery = {}): Observable<PlatformInvitationPage> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 25 };
    if (params.q?.trim()) query['q'] = params.q.trim();
    if (params.type && params.type !== 'ALL') query['type'] = params.type;
    if (params.status && params.status !== 'ALL') query['status'] = params.status;
    return this.http.get<PlatformInvitationPage>(`${this.propertyBase}/platform/invitations`, { params: query, withCredentials: true });
  }

  getPlatformInvitationSummary(): Observable<PlatformInvitationSummary> {
    return this.http.get<PlatformInvitationSummary>(`${this.propertyBase}/platform/invitations/summary`, { withCredentials: true });
  }

  platformInvitationAction(
    invitation: Pick<PlatformInvitation, 'type' | 'id'>,
    action: 'resend' | 'revoke',
    reason: string,
  ): Observable<PlatformInvitation> {
    return this.http.post<PlatformInvitation>(
      `${this.propertyBase}/platform/invitations/${invitation.type.toLowerCase()}/${encodeURIComponent(invitation.id)}/${action}`,
      { reason },
      { withCredentials: true },
    );
  }

  getPlatformPayments(params: PlatformPaymentQuery = {}): Observable<PlatformPaymentPage> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 25 };
    for (const key of ['q', 'status', 'type', 'method', 'orgId', 'from', 'to'] as const) {
      const value = params[key]?.toString().trim();
      if (value && value !== 'ALL') query[key] = value;
    }
    return this.http.get<PlatformPaymentPage>(`${this.paymentsBase}/platform/payments`, { params: query, withCredentials: true });
  }

  getPlatformPaymentSummary(range: { from?: string | null; to?: string | null; orgId?: string | null } = {}): Observable<PlatformPaymentSummary> {
    const query: Record<string, string> = {};
    if (range.from) query['from'] = range.from;
    if (range.to) query['to'] = range.to;
    if (range.orgId) query['orgId'] = range.orgId;
    return this.http.get<PlatformPaymentSummary>(`${this.paymentsBase}/platform/payments/summary`, { params: query, withCredentials: true });
  }

  private normalizeAddressSuggestion(suggestion: PlatformAddressSuggestion): PlatformAddressSuggestion {
    const normalized = this.normalizeAddress(suggestion);
    return {
      ...suggestion,
      ...normalized,
      label: suggestion.label || normalized.formattedAddress || normalized.line1 || '',
    };
  }

  private accountDeletionAction(
    requestId: string,
    action: 'review' | 'approve' | 'reject' | 'complete',
    payload: PlatformAccountDeletionActionRequest,
  ): Observable<PlatformAccountDeletionRequest> {
    return this.http.post<PlatformAccountDeletionRequest>(
      `${this.base}/api/v1/account-deletion-requests/platform/${encodeURIComponent(requestId)}/${action}`,
      payload,
      { withCredentials: true },
    );
  }

  private normalizeAddress<T extends PlatformNormalizedAddress | PlatformAddressSuggestion>(address: T): T {
    return {
      ...address,
      formattedAddress: address.formattedAddress || address.formatted_address || null,
      line1: address.line1 || address.address_line1 || null,
      city: address.city || null,
      municipality: address.municipality || null,
      state: address.state || null,
      stateCode: address.stateCode || address.state_code || null,
      postalCode: address.postalCode || address.postcode || address.postal_code || null,
      country: address.country || null,
      countryCode: address.countryCode || address.country_code || null,
      placeId: address.placeId || address.place_id || null,
    };
  }

  logout(): Observable<string> {
    return this.http.post(`${this.base}/api/v1/user/logout`, {}, {
      responseType: 'text',
      withCredentials: true,
    });
  }

  private refreshSession(): Observable<unknown> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post(`${this.base}/api/v1/user/refresh`, {}, {
        responseType: 'text',
        withCredentials: true,
      }).pipe(
        timeout(8000),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshInFlight$;
  }

  private unauthenticatedSession(): PlatformAdminSession {
    return {
      authenticated: false,
      roles: [],
    };
  }

  private clearSessionVerification(): void {
    this.verifySessionInFlight$ = null;
  }

  private wait(delayMs: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  private isAuthExpired(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
    const message = String(error.error?.message ?? '').toLowerCase();
    const url = String(error.url ?? '').toLowerCase();
    const authEndpoint = url.includes('/api/v1/user/');

    return (
      error.status === 401 ||
      error.status === 419 ||
      errorCode === 'AUTHORIZATION_FAILED' ||
      (error.status === 403 && authEndpoint && (message.includes('authorization failed') || message.includes('no cookies')))
    );
  }

  private isMissingCookieAuthVerify(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const url = String(error.url ?? '').toLowerCase();
    if (!url.includes('/api/v1/user/auth-verify')) {
      return false;
    }

    const cookieCount = error.headers?.get('X-Auth-Cookie-Count');
    const message = String(error.error?.message ?? '').toLowerCase();

    return cookieCount === '0'
      || message.includes('no cookies')
      || (error.status === 401 && !error.headers?.get('X-Auth-Cookie-Names'));
  }

  private logAuthHeaders(label: string, response: HttpResponse<unknown>): void {
    if (typeof window === 'undefined') {
      return;
    }

    const isLocalAdmin = window.location.hostname === 'localhost' && window.location.port === '4600';
    if (!isLocalAdmin) {
      return;
    }

    console.info('[PlatformAdminAuth]', label, {
      url: response.url,
      status: response.status,
      setCookieCount: response.headers.get('X-Auth-Set-Cookie-Count'),
      setCookieNames: response.headers.get('X-Auth-Set-Cookie-Names'),
      requestCookieCount: response.headers.get('X-Auth-Cookie-Count'),
      requestCookieNames: response.headers.get('X-Auth-Cookie-Names'),
    });
  }
}

function auditParams(query: PlatformAuditQuery): HttpParams {
  let params = new HttpParams();
  if (query.q?.trim()) params = params.set('q', query.q.trim());
  if (query.event && query.event !== 'ALL') params = params.set('event', query.event);
  if (query.from) params = params.set('from', query.from);
  if (query.to) params = params.set('to', query.to);
  return params;
}

function reportParams(range: ReportRange, orgId: string | null): HttpParams {
  const params = new HttpParams().set('from', range.from).set('to', range.to);
  return orgId ? params.set('orgId', orgId) : params;
}
